import { useState } from "react";
import { Link } from "react-router";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@nosible/shared/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@nosible/shared/components/ui/table";
import { Badge } from "@nosible/shared/components/ui/badge";
import { Button } from "@nosible/shared/components/ui/button";
import { trpc } from "@/admin/lib/trpc";
import { AdminCreateOrg } from "@/admin/components/AdminCreateOrg";
import { ChevronLeft, ChevronRight, Building2, Users } from "lucide-react";

export function AdminOrgs() {
  const [page, setPage] = useState(0);
  const pageSize = 10;

  const {
    data: orgsData,
    isLoading,
    error,
  } = trpc.admin.listOrganizations.useQuery(
    {
      limit: pageSize,
      offset: page * pageSize,
    },
    {
      enabled: true,
    }
  );

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-silica">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-red-400">Error loading organizations: {error.message}</div>
      </div>
    );
  }

  const totalPages = Math.ceil((orgsData?.total ?? 0) / pageSize);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-2 mb-6">
        <Building2 className="h-6 w-6 text-violet-pulse" />
        <h1 className="text-2xl font-bold text-silica font-display">Admin: Organizations</h1>
        <div className="flex-1" />
        <AdminCreateOrg />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Organizations</CardTitle>
          <CardDescription>
            Manage all organizations in the system. Total: {orgsData?.total ?? 0} organizations
          </CardDescription>
        </CardHeader>
        <CardContent>
          {orgsData?.organizations.length === 0 ? (
            <div className="text-center py-8 text-chromium-mist">
              <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No organizations found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Members</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orgsData?.organizations.map(
                  (org: NonNullable<typeof orgsData>["organizations"][number]) => (
                    <TableRow key={org.id}>
                      <TableCell className="font-medium text-silica">
                        <div className="flex items-center gap-2">
                          {org.logo ? (
                            <img
                              src={org.logo}
                              alt={org.name}
                              className="h-8 w-8 rounded-md object-cover"
                            />
                          ) : (
                            <div className="h-8 w-8 rounded-md bg-slate-core flex items-center justify-center">
                              <Building2 className="h-4 w-4 text-chromium-mist" />
                            </div>
                          )}
                          {org.name}
                        </div>
                      </TableCell>
                      <TableCell className="text-chromium-mist font-mono text-sm">
                        {org.slug}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="flex items-center gap-1 w-fit">
                          <Users className="h-3 w-3" />
                          {org.memberCount}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-chromium-mist">
                        {org.createdAt
                          ? new Date(org.createdAt).toLocaleDateString()
                          : "—"}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" asChild>
                          <Link to={`/organizations/${org.slug}`}>
                            View
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                )}
              </TableBody>
            </Table>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-core">
              <div className="text-sm text-chromium-mist">
                Page {page + 1} of {totalPages}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
